/**
 * ShadowClaw — Group (conversation) metadata management
 * Stores group metadata in the config store as JSON.
 */

import { DEFAULT_GROUP_ID } from "../config/config.js";
import { getConfig } from "./getConfig.js";
import { setConfig } from "./setConfig.js";
import { ulid } from "../utils/ulid.js";
import type { GroupMeta, ShadowClawDatabase } from "./types.js";

const CONFIG_KEY = "group_metadata";

/**
 * Get all group metadata from config.
 */
export async function getGroupMetadata(
  db: ShadowClawDatabase,
): Promise<GroupMeta[]> {
  const raw = await getConfig(db, CONFIG_KEY);
  if (!raw) {
    return [];
  }

  try {
    return JSON.parse(raw);
  } catch {
    return [];
  }
}

/**
 * Save group metadata to config.
 */
export async function saveGroupMetadata(
  db: ShadowClawDatabase,
  metadata: GroupMeta[],
): Promise<void> {
  await setConfig(db, CONFIG_KEY, JSON.stringify(metadata));
}

/**
 * Create a new group and persist it.
 */
export async function createGroup(
  db: ShadowClawDatabase,
  name: string,
  prefix: string = "br:",
  id?: string,
): Promise<GroupMeta> {
  const groups = await getGroupMetadata(db);
  const group: GroupMeta = {
    groupId: `${prefix}${id ?? ulid()}`,
    name,
    createdAt: Date.now(),
  };
  groups.push(group);
  await saveGroupMetadata(db, groups);

  return group;
}

/**
 * Rename an existing group.
 */
export async function renameGroup(
  db: ShadowClawDatabase,
  groupId: string,
  newName: string,
): Promise<void> {
  const groups = await getGroupMetadata(db);
  const group = groups.find((g) => g.groupId === groupId);
  if (group) {
    group.name = newName;
  }

  await saveGroupMetadata(db, groups);
}

/**
 * Update the tool tags pinned to an existing group.
 */
export async function updateGroupToolTags(
  db: ShadowClawDatabase,
  groupId: string,
  tags?: string[],
): Promise<void> {
  const groups = await getGroupMetadata(db);
  const group = groups.find((g) => g.groupId === groupId);
  if (group) {
    if (tags && tags.length > 0) {
      group.toolTags = tags;
    } else {
      delete group.toolTags;
    }
  }

  await saveGroupMetadata(db, groups);
}

/**
 * Delete a group from metadata.
 */
export async function deleteGroupMetadata(
  db: ShadowClawDatabase,
  groupId: string,
): Promise<void> {
  const groups = await getGroupMetadata(db);
  const filtered = groups.filter((g) => g.groupId !== groupId);
  await saveGroupMetadata(db, filtered);
}

/**
 * List all groups in their persisted order.
 * If no groups exist, returns a default "Main" group.
 */
export async function listGroups(db: ShadowClawDatabase): Promise<GroupMeta[]> {
  const groups = await getGroupMetadata(db);
  if (groups.length === 0) {
    const defaultGroup: GroupMeta = {
      groupId: DEFAULT_GROUP_ID,
      name: "Main",
      createdAt: 0,
    };
    await saveGroupMetadata(db, [defaultGroup]);

    return [defaultGroup];
  }

  return groups;
}

/**
 * Reorder groups according to a provided list of groupIds.
 * Groups not in the list are appended at the end in their original order.
 */
export async function reorderGroups(
  db: ShadowClawDatabase,
  orderedIds: string[],
): Promise<void> {
  const groups = await getGroupMetadata(db);
  const byId = new Map(groups.map((g) => [g.groupId, g]));

  const reordered: GroupMeta[] = [];
  for (const id of orderedIds) {
    const g = byId.get(id);
    if (g) {
      reordered.push(g);
      byId.delete(id);
    }
  }

  // Append any groups not mentioned in orderedIds
  for (const g of groups) {
    if (byId.has(g.groupId)) {
      reordered.push(g);
    }
  }

  await saveGroupMetadata(db, reordered);
}

/**
 * Clone a group (metadata only). Messages must be cloned separately.
 * Returns null if the source group is not found.
 */
export async function cloneGroup(
  db: ShadowClawDatabase,
  sourceGroupId: string,
): Promise<GroupMeta | null> {
  const groups = await getGroupMetadata(db);
  const source = groups.find((g) => g.groupId === sourceGroupId);
  if (!source) {
    return null;
  }

  // Extract prefix from the source groupId (e.g. "br:" from "br:abc123")
  const colonIdx = sourceGroupId.indexOf(":");
  const prefix = colonIdx >= 0 ? sourceGroupId.slice(0, colonIdx + 1) : "br:";

  const clone: GroupMeta = {
    groupId: `${prefix}${ulid()}`,
    name: `${source.name} (copy)`,
    createdAt: Date.now(),
    pinnedMaxTokens: source.pinnedMaxTokens,
    providerRuntimeOverrides: source.providerRuntimeOverrides
      ? JSON.parse(JSON.stringify(source.providerRuntimeOverrides))
      : undefined,
    subagentModelSelectionMode: source.subagentModelSelectionMode,
    subagentMaxTokens: source.subagentMaxTokens,
    subagentPinnedProvider: source.subagentPinnedProvider,
    subagentPinnedModel: source.subagentPinnedModel,
    subagentFastProvider: source.subagentFastProvider,
    subagentFastModel: source.subagentFastModel,
    subagentSmartProvider: source.subagentSmartProvider,
    subagentSmartModel: source.subagentSmartModel,
    subagentPowerfulProvider: source.subagentPowerfulProvider,
    subagentPowerfulModel: source.subagentPowerfulModel,
    toolTags: source.toolTags ? [...source.toolTags] : undefined,
  };
  groups.push(clone);
  await saveGroupMetadata(db, groups);

  return clone;
}

/**
 * Update the pinned provider and model for an existing group.
 */
export async function updateGroupPinnedProvider(
  db: ShadowClawDatabase,
  groupId: string,
  providerId?: string,
  modelId?: string,
  pinnedMaxTokens?: number,
): Promise<void> {
  const groups = await getGroupMetadata(db);
  const group = groups.find((g) => g.groupId === groupId);
  if (group) {
    if (providerId) {
      group.pinnedProvider = providerId;
    } else {
      delete group.pinnedProvider;
    }

    if (modelId) {
      group.pinnedModel = modelId;
    } else {
      delete group.pinnedModel;
    }

    if (
      typeof pinnedMaxTokens === "number" &&
      Number.isFinite(pinnedMaxTokens) &&
      pinnedMaxTokens > 0
    ) {
      group.pinnedMaxTokens = Math.floor(pinnedMaxTokens);
    } else {
      delete group.pinnedMaxTokens;
    }
  }

  await saveGroupMetadata(db, groups);
}

/**
 * Update group-level defaults for subagent provider/model selection.
 */
export async function updateGroupSubagentSettings(
  db: ShadowClawDatabase,
  groupId: string,
  mode: "automatic" | "manual",
  providerId?: string,
  modelId?: string,
  subagentMaxTokens?: number,
): Promise<void> {
  const groups = await getGroupMetadata(db);
  const group = groups.find((g) => g.groupId === groupId);
  if (group) {
    group.subagentModelSelectionMode = mode;

    if (mode === "manual") {
      if (providerId) {
        group.subagentPinnedProvider = providerId;
      } else {
        delete group.subagentPinnedProvider;
      }

      if (modelId) {
        group.subagentPinnedModel = modelId;
      } else {
        delete group.subagentPinnedModel;
      }
    } else {
      delete group.subagentPinnedProvider;
      delete group.subagentPinnedModel;
    }

    if (
      typeof subagentMaxTokens === "number" &&
      Number.isFinite(subagentMaxTokens) &&
      subagentMaxTokens > 0
    ) {
      group.subagentMaxTokens = Math.floor(subagentMaxTokens);
    } else {
      delete group.subagentMaxTokens;
    }
  }

  await saveGroupMetadata(db, groups);
}

/**
 * Update group-level provider runtime overrides (for provider module settings).
 */
export async function updateGroupProviderRuntimeOverrides(
  db: ShadowClawDatabase,
  groupId: string,
  overrides: GroupMeta["providerRuntimeOverrides"],
): Promise<void> {
  const groups = await getGroupMetadata(db);
  const group = groups.find((g) => g.groupId === groupId);
  if (group) {
    if (overrides && Object.keys(overrides).length > 0) {
      group.providerRuntimeOverrides = JSON.parse(JSON.stringify(overrides));
    } else {
      delete group.providerRuntimeOverrides;
    }
  }

  await saveGroupMetadata(db, groups);
}

/**
 * Update the named subagent model profile slots (fast / smart / powerful).
 * These are available in automatic mode so the LLM can route subagents by
 * task complexity without hallucinating arbitrary provider/model IDs.
 */
export async function updateGroupSubagentProfiles(
  db: ShadowClawDatabase,
  groupId: string,
  profiles: {
    fast?: { provider?: string; model?: string } | null;
    smart?: { provider?: string; model?: string } | null;
    powerful?: { provider?: string; model?: string } | null;
  },
): Promise<void> {
  const groups = await getGroupMetadata(db);
  const group = groups.find((g) => g.groupId === groupId);
  if (!group) {
    return;
  }

  const set = (
    providerKey: keyof GroupMeta,
    modelKey: keyof GroupMeta,
    slot: { provider?: string; model?: string } | null | undefined,
  ) => {
    if (slot === null) {
      // Explicit null clears the slot
      delete (group as any)[providerKey];
      delete (group as any)[modelKey];
    } else if (slot !== undefined) {
      if (slot.provider) {
        (group as any)[providerKey] = slot.provider;
      } else {
        delete (group as any)[providerKey];
      }

      if (slot.model) {
        (group as any)[modelKey] = slot.model;
      } else {
        delete (group as any)[modelKey];
      }
    }
  };

  set("subagentFastProvider", "subagentFastModel", profiles.fast);
  set("subagentSmartProvider", "subagentSmartModel", profiles.smart);
  set("subagentPowerfulProvider", "subagentPowerfulModel", profiles.powerful);

  await saveGroupMetadata(db, groups);
}
